import {
  IconCalendar,
  IconClock,
  IconGenderFemale,
  IconGenderMale,
  IconGenderTransgender,
  IconMapPin, // <--- Imported Icon
  IconTrash,
  IconUser
} from "@tabler/icons-react";
import styles from "./ProfileCard.module.css";

export interface EnrichedProfile {
  id: string;
  name: string;
  preferred_language: string | null;
  gender: string;
  date_of_birth: string;
  time_of_birth: string;
  birth_place: string;
}

interface ProfileCardProps {
  profile: EnrichedProfile;
  index: number;
  isCurrent?: boolean;
  onClick: (profile: EnrichedProfile) => void;
  onDelete?: (profile: EnrichedProfile) => void;
}

const formatDate = (dateString: string) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (timeString: string) => {
  if (!timeString || !timeString.includes(":")) return "N/A";
  const [h, m] = timeString.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  let hour = h % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
};

const getGenderIcon = (gender: string) => {
  if (gender === "Male")
    return <IconGenderMale size={14} className={styles.maleIcon} />;
  if (gender === "Female")
    return <IconGenderFemale size={14} className={styles.femaleIcon} />;
  return <IconGenderTransgender size={14} className={styles.otherIcon} />;
};

export default function ProfileCard({
  profile,
  index,
  isCurrent = false,
  onClick,
  onDelete,
}: ProfileCardProps) {
  return (
    <div
      className={`${styles.profileCard} ${isCurrent ? styles.currentProfile : ""}`}
      onClick={() => onClick(profile)}
    >
      {/* --- DELETE BUTTON (soft-delete, mirrors the app's per-profile delete) --- */}
      {onDelete && (
        <button
          className={styles.deleteButton}
          onClick={(e) => {
            e.stopPropagation(); // don't trigger the card's edit-on-click
            onDelete(profile);
          }}
          aria-label={`Delete ${profile.name}`}
          title="Delete profile"
        >
          <IconTrash size={16} />
        </button>
      )}

      <div className={styles.cardHeader}>
        <div className={styles.avatar}>
          <IconUser size={24} className={styles.avatarIcon} />
          <span className={styles.avatarNumber}>{index + 1}</span>
        </div>
        <div className={styles.nameContainer}>
          <span className={styles.name}>{profile.name}</span>
          {isCurrent && <span className={styles.tag}>Current Profile</span>}
        </div>
      </div>
      <div className={styles.cardDetails}>
        <div className={styles.detailItem}>
          {getGenderIcon(profile.gender)}
          <span className={styles.detailText}>{profile.gender}</span>
        </div>
        <div className={styles.detailItem}>
          <IconCalendar size={14} className={styles.dobIcon} />
          <span className={styles.detailText}>
            {formatDate(profile.date_of_birth)}
          </span>
        </div>
        <div className={styles.detailItem}>
          <IconClock size={14} className={styles.tobIcon} />
          <span className={styles.detailText}>
            {formatTime(profile.time_of_birth)}
          </span>
        </div>
        <div className={styles.detailItem}>
          <IconMapPin size={14} className={styles.pobIcon} />
          <span className={styles.detailText}>
            {/* Guarded like formatDate/formatTime above. A user_profiles row
                can exist before its user_birth_details row does (the calculator
                creates the profile first), so birth_place is undefined for a
                window - and unguarded .split() here threw inside render and
                took the whole page down through the error boundary. The type
                says `string`, which is why the compiler never flagged it. */}
            {profile.birth_place
              ? profile.birth_place.split(",")[0]
              : "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
}
